import React, { useRef } from 'react';
import { DocEditor } from '../../DocEditor';
import { useSnippetBacklinks } from '../../../hooks/useSnippetBacklinks';
import DocSkeleton from '../DocSkeleton';
import LibraryDocChrome from './components/LibraryDocChrome';
import { useLibraryDocLoad } from './hooks/useLibraryDocLoad';
import { useLibraryDocAutosave } from './hooks/useLibraryDocAutosave';
import { useLibraryDocTicks } from './hooks/useLibraryDocTicks';
import { useLibraryDocFiles } from './hooks/useLibraryDocFiles';
import { useLibraryDocVariables } from './hooks/useLibraryDocVariables';
import { useLibraryDocMeta } from './hooks/useLibraryDocMeta';
import './library-doc-pane.css';
import '../doc-skeleton.css';

/**
 * LibraryDocPane — a library doc rendered INLINE (no modal chrome).
 *
 * Extracted from the retired DocPeekOverlay: same fetch/autosave/tick
 * semantics, but the host decides where it appears — today that's a doc TAB
 * inside the Mission Full View's main column (no more modal-on-modal).
 *
 * A doc is ALWAYS just the doc (revised model, chunk 11): content edits made
 * here autosave to the one master via SopAPI, exactly like the library full
 * page — editing from a mission updates the doc everywhere.
 *
 * TICKS are the one exception (copy-on-write, Chunk 11b): the master's
 * checkbox state is the shared DEFAULT; the mission stores only OVERRIDES
 * for steps it has diverged on (`tickOverrides` in, `onTicksChange` out —
 * keyed by each checkbox's stable block id, so reordering steps in the
 * library keeps a mission's ticks on the right ones). Ticks are NEVER saved
 * onto the doc — the save path pins every checkbox back to the master's
 * value before writing. Steps the mission never touched keep following the
 * master's defaults.
 *
 * FILE UPLOADS are the same idea: the block is the shared slot; files live
 * on the mission (`fileOverrides` / `onFilesChange`) and never write into
 * the doc body.
 *
 * VARIABLE VALUES follow each variable's scope. This document saves on
 * `doc.variables`. This mission saves on `task.variableValues`. Campaign
 * variables are rows on the campaign and are inherited.
 */

const LibraryDocPane = ({
  docId,
  /** This mission's saved tick overrides for this doc: { blockId: boolean } */
  tickOverrides,
  /** Reports the CURRENT full override set (may be {}) whenever ticks change */
  onTicksChange,
  /** This mission's uploaded files for this doc: { blockId: FileRef[] } */
  fileOverrides,
  /** Reports the current file map whenever an upload field changes */
  onFilesChange,
  /** Mission id — required for upload/download against the attachment API */
  missionId,
  /** A mention inside THIS doc was clicked — host opens/activates that doc */
  onOpenDoc,
  /** Mention metadata refresh + instant "/doc" picker (same as DocFullPage) */
  resolveDocMeta,
  availableDocs = null,
  /** Hide the tiny autosave status line (defaults to shown) */
  showSaveState = true,
  /**
   * Variable context (optional): the LIVE campaign this doc is being viewed
   * through — { id, name, variables, parentSprintId } — plus the sprint list
   * for walking ancestors. Values resolve root campaign → … → campaign →
   * mission → doc, highest level wins; outside a campaign only doc-scoped
   * variables resolve.
   */
  variableCampaign = null,
  allSprints = [],
  /** This mission's value overlays. Set (even []) only when opened from a mission. */
  missionVariables = null,
  onMissionVariablesChange = null,
  // Campaign-owned rows stay read-only in the in-doc table unless the host
  // provides a patch handler (confirm-to-edit inherited values).
  onCampaignVariablesChange = null,
  onPatchInheritedVariable = null,
  /** Skip line shimmer when a saved scroll is being restored under a cover. */
  hideBodySkeleton = false,
}) => {
  const { backlinksByBlockId } = useSnippetBacklinks(docId);
  // Read only inside the per-docId load effect, so prop echoes while typing
  // can't fight local state — but doc swaps (mention click) get fresh values
  const tickOverridesRef = useRef(tickOverrides);
  tickOverridesRef.current = tickOverrides;
  const metaCacheRef = useRef(new Map());

  const {
    doc,
    setDoc,
    blocks,
    setBlocks,
    title,
    setTitle,
    loadError,
    docVariables,
    setDocVariables,
    variablesColumnWidths,
    setVariablesColumnWidths,
    localTickOverrides,
    setLocalTickOverrides,
    masterTicksRef,
    lastSavedBodyRef,
    lastReportedTicksRef,
  } = useLibraryDocLoad(docId, tickOverridesRef);

  const { saveState, setSaveState, handleBlocksChange } = useLibraryDocAutosave({
    docId,
    masterTicksRef,
    lastSavedBodyRef,
    setBlocks,
  });

  const { handleTickChange, handleTickOverridesChange } = useLibraryDocTicks({
    onTicksChange,
    masterTicksRef,
    lastReportedTicksRef,
    localTickOverrides,
    setLocalTickOverrides,
    tickOverrides,
    docId,
    missionId,
  });

  const { localFileOverrides, handleFilesChange } = useLibraryDocFiles({
    fileOverrides,
    onFilesChange,
    docId,
    missionId,
  });

  const {
    scopedVariables,
    resolveVariable,
    handleDocVariablesChange,
    handleVariablesColumnWidthsChange,
    handleCreateVariable,
  } = useLibraryDocVariables({
    docId,
    docVariables,
    setDocVariables,
    setVariablesColumnWidths,
    variableCampaign,
    allSprints,
    missionVariables,
  });

  const { handleTitleChange, handleTitleKeyDown, handleIconChange } = useLibraryDocMeta({
    docId,
    setDoc,
    title,
    setTitle,
    blocks,
    handleBlocksChange,
    setSaveState,
  });

  const saveLabel =
    saveState === 'saving' ? 'Saving…'
    : saveState === 'dirty' ? 'Unsaved changes'
    : saveState === 'saved' ? 'Saved to library'
    : saveState === 'error' ? 'Save failed — retrying on next edit'
    : '';

  const listedMeta = resolveDocMeta?.(docId);
  if (doc?.id) {
    metaCacheRef.current.set(doc.id, { title: doc.title, icon: doc.icon });
  }
  const meta = listedMeta || metaCacheRef.current.get(docId) || null;
  // Ignore the previous doc on the first paint after a switch — otherwise
  // chrome briefly shows the old title, then '' ("Untitled Doc") + a grey icon.
  const docReady = Boolean(doc?.id === docId);
  const chromeIcon = docReady ? (doc.icon ?? meta?.icon) : meta?.icon;
  const chromeTitle = docReady ? title : (meta?.title || '');
  const hasMetaHint = Boolean(meta?.title || meta?.icon != null);

  if (loadError) {
    return (
      <div className="library-doc-pane__missing">
        {loadError.includes('404') || /not found/i.test(loadError)
          ? 'This doc could not be found — it may have been deleted.'
          : loadError}
      </div>
    );
  }

  const hideUnderCover = hideBodySkeleton && !docReady;
  const useFullSkeleton = !docReady && !hasMetaHint && !hideUnderCover;

  return (
    <article className="library-doc-pane">
      {useFullSkeleton && <DocSkeleton variant="pane" />}
      {!hideUnderCover && !useFullSkeleton && (
        <>
          <LibraryDocChrome
            doc={docReady ? doc : null}
            chromeIcon={chromeIcon}
            chromeTitle={chromeTitle}
            showSaveState={showSaveState}
            saveLabel={saveLabel}
            saveState={saveState}
            onTitleChange={handleTitleChange}
            onTitleKeyDown={handleTitleKeyDown}
            onIconChange={handleIconChange}
          />
          {docReady ? (
            <DocEditor
              docId={docId}
              blocks={blocks}
              onChange={handleBlocksChange}
              tickOverrides={localTickOverrides}
              onTickChange={handleTickChange}
              onTickOverridesChange={handleTickOverridesChange}
              fileOverrides={localFileOverrides}
              onFilesChange={missionId ? handleFilesChange : null}
              missionId={missionId}
              onOpenDoc={onOpenDoc}
              resolveDocMeta={resolveDocMeta}
              availableDocs={availableDocs}
              snippetBacklinks={backlinksByBlockId}
              resolveVariable={resolveVariable}
              availableVariables={scopedVariables}
              onCreateVariable={handleCreateVariable}
              docVariables={docVariables}
              onDocVariablesChange={handleDocVariablesChange}
              variableCampaign={variableCampaign}
              allSprints={allSprints}
              onCampaignVariablesChange={onCampaignVariablesChange}
              missionVariables={missionVariables}
              onMissionVariablesChange={onMissionVariablesChange}
              onPatchInheritedVariable={onPatchInheritedVariable}
              variablesColumnWidths={variablesColumnWidths}
              onVariablesColumnWidthsChange={handleVariablesColumnWidthsChange}
            />
          ) : (
            <DocSkeleton bodyOnly />
          )}
        </>
      )}
    </article>
  );
};

export default LibraryDocPane;
